import { Card, Skeleton, useApp } from '@startupquickstart/frontend';
import { APIProvider, AdvancedMarker, Map, useMap } from '@vis.gl/react-google-maps';
import { MapPin, Users } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import {
  useGrantTogetherOverride,
  useTeamLocations,
  type TeamLocation,
} from '@/components/events/live/useTeamLocations';
import {
  describeSplitTeams,
  formatAge,
  splitTeams,
} from '@/components/events/live/teamLocationDisplay';
import { DEFAULT_TEAM_ICON_COLOR, getTeamIcon } from '@/utils/teamIcons';
import { cn } from '@/lib/utils';

/** One team's pin: its icon in its own colour, so it matches the scoreboard. */
function TeamPin({ team, dimmed }: { team: TeamLocation; dimmed: boolean }) {
  const Icon = getTeamIcon(team.iconName);
  return (
    <div
      className={cn(
        'flex h-8 w-8 items-center justify-center rounded-full border-2 border-white shadow-md',
        dimmed && 'opacity-50',
      )}
      style={{ backgroundColor: team.iconColor || DEFAULT_TEAM_ICON_COLOR }}
      title={team.name}
    >
      <Icon className="h-4 w-4 text-white" />
    </div>
  );
}

/** Fit the viewport to whoever is currently reporting. */
function MapBoundsController({ points }: { points: { lat: number; lng: number }[] }) {
  const map = useMap();
  // Only refit when the *set* of teams changes, not on every position
  // nudge — otherwise the map jitters under the facilitator's cursor
  // every poll and can't be panned or zoomed manually.
  const signature = points.length;

  useEffect(() => {
    if (!map || points.length === 0) return;
    if (points.length === 1) {
      map.setCenter(points[0]!);
      map.setZoom(16);
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    points.forEach((p) => bounds.extend(p));
    map.fitBounds(bounds, 64);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, signature]);

  return null;
}

interface TeamLocationsPanelProps {
  eventId: string;
  /**
   * Whether this event refuses scoring for split teams. Drives the
   * override button — without enforcement a split is only advisory, so
   * there is nothing to grant a pass from.
   */
  enforcesTogetherness?: boolean;
  className?: string;
}

/**
 * Where every team is right now.
 *
 * Facilitators asked for this directly — the old SmartHunts workflow let
 * staff see teams on the ground, and without it nobody knows who is lost,
 * who is stationary, or who has drifted out of the play area.
 *
 * Shows one pin per team (the leader's device, or the freshest fix), and
 * flags any team whose members have spread far apart, which is the signal
 * behind a "stay together" rule.
 */
export function TeamLocationsPanel({
  eventId,
  enforcesTogetherness = false,
  className,
}: TeamLocationsPanelProps) {
  const { config } = useApp();
  const { data, isLoading } = useTeamLocations(eventId);
  const grantOverride = useGrantTogetherOverride(eventId);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);

  const teams = useMemo(() => data?.data ?? [], [data]);
  const located = useMemo(() => teams.filter((t) => t.position !== null), [teams]);
  const points = useMemo(
    () => located.map((t) => ({ lat: t.position!.latitude, lng: t.position!.longitude })),
    [located],
  );
  const split = useMemo(() => splitTeams(teams), [teams]);
  // One instant for the whole render, so rows don't disagree about "now".
  const now = Date.now();

  const header = (
    <div className="flex items-center justify-between border-b px-4 py-3">
      <h3 className="font-mono text-[0.6875rem] font-medium tracking-[0.15em] text-muted-foreground uppercase">
        Team locations
      </h3>
      <span className="text-xs text-muted-foreground">
        {located.length} of {teams.length} reporting
      </span>
    </div>
  );

  if (isLoading) {
    return (
      <Card className={cn('gap-0 overflow-hidden py-0', className)}>
        {header}
        <Skeleton className="h-64 rounded-none" />
      </Card>
    );
  }

  return (
    <Card className={cn('gap-0 overflow-hidden py-0', className)}>
      {header}

      {!config?.googleMapsApiKey ? (
        <div className="flex h-64 flex-col items-center justify-center text-muted-foreground">
          <MapPin className="mb-1 h-6 w-6" />
          <span className="text-sm">Map unavailable — no API key configured</span>
        </div>
      ) : located.length === 0 ? (
        // Distinct from "no API key": the map works, nobody is reporting.
        // Usually means the hunt hasn't started or nobody granted location.
        <div className="flex h-64 flex-col items-center justify-center text-muted-foreground">
          <Users className="mb-1 h-6 w-6" />
          <span className="text-sm">No teams reporting their position yet</span>
          <span className="mt-1 text-xs">
            Teams appear once the hunt is running and they allow location access
          </span>
        </div>
      ) : (
        <div className="h-64">
          <APIProvider apiKey={config.googleMapsApiKey}>
            <Map
              mapId="team-locations"
              defaultCenter={points[0]}
              defaultZoom={14}
              gestureHandling="greedy"
              disableDefaultUI
              zoomControl
              style={{ width: '100%', height: '100%' }}
            >
              <MapBoundsController points={points} />
              {located.map((team) => (
                <AdvancedMarker
                  key={team.teamId}
                  position={{ lat: team.position!.latitude, lng: team.position!.longitude }}
                  onClick={() =>
                    setSelectedTeamId((current) => (current === team.teamId ? null : team.teamId))
                  }
                >
                  <TeamPin team={team} dimmed={false} />
                </AdvancedMarker>
              ))}
            </Map>
          </APIProvider>
        </div>
      )}

      {split.length > 0 && (
        <div className="space-y-2 border-t bg-amber-50 px-4 py-2 dark:bg-amber-950/30">
          <p className="text-xs text-amber-800 dark:text-amber-200">{describeSplitTeams(split)}</p>
          {/* Escape hatch: when the rule is wrong and the team is standing
              in front of staff, a time-boxed pass beats arguing with GPS.
              Only rendered while the event actually enforces the rule —
              otherwise a split is advisory and there is nothing to grant. */}
          {enforcesTogetherness && (
            <div className="flex flex-wrap gap-2">
              {split.map((team) => (
                <button
                  key={team.teamId}
                  type="button"
                  disabled={grantOverride.isPending}
                  onClick={() => grantOverride.mutate({ teamId: team.teamId, minutes: 30 })}
                  className="rounded-md border border-amber-300 bg-white px-2 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50 dark:border-amber-800 dark:bg-transparent dark:text-amber-100"
                >
                  Let {team.name} score for 30 min
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <ul className="max-h-48 divide-y overflow-auto">
        {teams.map((team) => {
          const Icon = getTeamIcon(team.iconName);
          const expanded = selectedTeamId === team.teamId;
          return (
            <li key={team.teamId}>
              <button
                type="button"
                onClick={() =>
                  setSelectedTeamId((current) => (current === team.teamId ? null : team.teamId))
                }
                className="flex w-full items-center gap-3 px-4 py-2 text-left hover:bg-muted/50"
                aria-expanded={expanded}
              >
                <span
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
                  style={{ backgroundColor: team.iconColor || DEFAULT_TEAM_ICON_COLOR }}
                >
                  <Icon className="h-3.5 w-3.5 text-white" />
                </span>
                <span className="min-w-0 flex-1 truncate text-sm">{team.name}</span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {team.position ? formatAge(team.position.updatedAt, now) : 'no signal'}
                </span>
              </button>

              {expanded && (
                <ul className="bg-muted/30 px-4 pb-2 pl-13">
                  {team.members.map((member) => (
                    <li
                      key={member.memberId}
                      className="flex items-center justify-between py-1 text-xs"
                    >
                      <span className="truncate text-muted-foreground">
                        {member.displayName}
                        {member.isLeader && ' (lead)'}
                      </span>
                      <span
                        className={cn(
                          'shrink-0',
                          member.live ? 'text-muted-foreground' : 'text-muted-foreground/60',
                        )}
                      >
                        {member.live ? formatAge(member.updatedAt, now) : 'no signal'}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
